// Organization for _error prop used by Redux API Middlware
/* eslint-disable  no-underscore-dangle */
/**
 * Middleware for handling various failure codes from middleware-base API
 * requests that should be handled in a consistent way throughout the application,
 * including preparing error messages to be consumed by redux form.
 */
import { replace } from 'connected-react-router';

import { enqueueSnackbar } from 'dux/notifications/actionCreators';
import { GENERIC_VALIDATION_ERROR, GENERIC_REQUEST_ERROR } from '../../constants/errors';

const ERROR_MAP = {
  'organization with this company name already exists.':
    'An organization with this name already exists.',
};

/**
 * Function for mapping API errors to an object that can be consumed by redux form
 *
 * @param {object} result - An API action result
 * @returns {object} - Object that represents the errors from the result
 *  payload mapped to a redux form consumable format including a form-level
 *  error message and field-specific error messages, all mapped from arrays to strings
 *  as needed.
 */
const getFormErrors = (result) => {
  let errObj = {};

  if (result.payload && result.payload.response && Object.keys(result.payload.response).length) {
    Object.keys(result.payload.response).reduce((acc, key) => {
      const val = result.payload.response[key];
      // Show first validation message if there are multiple returned in an array
      // The last else/if is for cockroachdb error messages
      if (Array.isArray(val)) {
        acc[key] = val.length ? val[0] : null;
      } else if (typeof val === 'string') {
        acc[key] = val;
      } else if (typeof val === 'object' && val.pgmessage) {
        acc[key] = val.pgmessage;
      }
      return acc;
    }, errObj);

    // Map the error messages to a more user-friendly version if they exist
    errObj = Object.keys(errObj).reduce((acc, key) => {
      acc[key] = ERROR_MAP[errObj[key]] || errObj[key];
      return acc;
    }, errObj);
  }
  // If no field-specific error messages were found, then show a generic error
  // The assumption is that if there is some prop other than [error, errors] in
  // the response payload, then there is at least one field-level validation
  // message to correct, and there's no need for a generic, form-wide error message.
  if (errObj.error) {
    errObj._error = errObj.error;
    delete errObj.error;
  } else if (errObj.errors) {
    errObj._error = errObj.errors;
    delete errObj.errors;
  } else if (errObj.nonFieldErrors) {
    errObj._error = errObj.nonFieldErrors;
    delete errObj.nonFieldErrors;
  } else if (errObj.detail) {
    errObj._error = errObj.detail;
    delete errObj.detail;
  } else {
    errObj._error = GENERIC_VALIDATION_ERROR;
  }
  return errObj;
};

/**
 * Middleware function that handles API errors and maps them to form errors as
 * appropriate.
 *
 * @param {object} store - The root store
 * @returns {function} - Middleware function that adds maps API errors to
 *  a formErrors prop or otherwise properly handlers redirects for various
 *  HTTP status errors.
 */
export default (store) => (next) => (action) => {
  const additionalProps = {};
  if (action.payload && action.payload.name === 'ApiError') {
    switch (action.payload.status) {
      case 400: // Bad Request -> map validation errors for redux forms
        additionalProps.formErrors = getFormErrors(action);
        // show non-field form errors in Snackbar
        if (action.payload.response.nonFieldErrors) {
          const errorMsg = action.payload.response.nonFieldErrors[0]
            ? action.payload.response.nonFieldErrors[0]
            : 'Error - Bad Request';
          store.dispatch(
            enqueueSnackbar({
              message: errorMsg,
              options: {
                variant: 'error',
              },
            }),
          );
        }

        // show snackbar if max limit reached
        if (
          typeof action.payload.response === 'string' &&
          action.payload.response.includes('Maximum number')
        ) {
          store.dispatch(
            enqueueSnackbar({
              message: action.payload.response,
              options: {
                variant: 'error',
              },
            }),
          );
        }
        break;

      default: {
        let { message } = action.payload;
        if (action.payload.response?.detail) {
          message += ` - ${action.payload.response.detail}`;
        }
        store.dispatch(
          enqueueSnackbar({
            message,
            options: {
              variant: 'error',
            },
          }),
        );
      }
    }
  } else if (action.payload && action.payload.name === 'RequestError') {
    // No connection to API
    switch (action.type) {
      case 'CREATE_TOKEN_FAILURE': // Form submission with no server connection -> Redirect to Error page
        store.dispatch(
          replace('/error/requestError', {
            from: store.getState().router.location.pathname,
          }),
        );
        break;
      default:
        additionalProps.formErrors = { _error: GENERIC_REQUEST_ERROR };
    }
  }
  return next({ ...action, ...additionalProps });
};
